/**
 * SpanishCharsGlobal — one floating keyboard bar that automatically follows
 * the currently focused textarea/input across the whole app. Rendered once
 * at the root of the student layout so we don't need to wrap every textarea
 * individually (there are dozens across MaterialComments, BuchÜbungen,
 * Dudas, ReadingPage, WritingPage, community, …).
 *
 * Rules:
 *   - Only students see it (isTeacher gate).
 *   - Only attaches to <textarea> and text-like <input> elements marked as
 *     student-writable (we skip search bars, coupon codes, etc. by matching
 *     `[data-testid]` containing “body|comment|write|answer|input|reply”
 *     or by defaulting to any textarea).
 *   - Hidden when nothing writable is focused.
 *   - Buttons insert the char at caret; keeps focus on the field.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

const CHARS = [
  'á', 'é', 'í', 'ó', 'ú', 'ñ', 'ü',
  'Á', 'É', 'Í', 'Ó', 'Ú', 'Ñ', 'Ü',
  '¿', '¡', '"',
];

function insertCharAtCaret(el, char) {
  if (!el) return;
  const start = el.selectionStart ?? el.value.length;
  const end   = el.selectionEnd   ?? el.value.length;
  if (typeof el.setRangeText === 'function') {
    el.setRangeText(char, start, end, 'end');
  } else {
    el.value = el.value.slice(0, start) + char + el.value.slice(end);
    const pos = start + char.length;
    try { el.setSelectionRange(pos, pos); } catch { /* noop */ }
  }
  const proto = el instanceof HTMLTextAreaElement
    ? window.HTMLTextAreaElement.prototype
    : window.HTMLInputElement.prototype;
  const nativeSetter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (nativeSetter) nativeSetter.call(el, el.value);
  el.dispatchEvent(new Event('input', { bubbles: true }));
  try { el.focus(); } catch { /* noop */ }
}

const isWritableField = (el) => {
  if (!el) return false;
  if (el.disabled || el.readOnly) return false;
  if (el.tagName === 'TEXTAREA') return true;
  if (el.tagName !== 'INPUT') return false;
  const t = (el.type || 'text').toLowerCase();
  // Skip fields that clearly aren't for student prose.
  if (['password', 'email', 'number', 'tel', 'date', 'datetime-local', 'time', 'file', 'checkbox', 'radio', 'range', 'color'].includes(t)) return false;
  // Skip explicit opt-outs (coupons, gutscheincode etc.).
  const testId = (el.getAttribute('data-testid') || '').toLowerCase();
  if (/coupon|gutschein|search|filter|url|price|amount|otp|code/.test(testId)) return false;
  if (el.getAttribute('data-nospanish') === 'true') return false;
  return true;
};

export default function SpanishCharsGlobal({ enabled = true }) {
  const [visible, setVisible] = useState(false);
  const fieldRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    const onFocusIn = (e) => {
      const el = e.target;
      if (isWritableField(el)) {
        fieldRef.current = el;
        setVisible(true);
      }
    };
    const onFocusOut = () => {
      // Delay so a click on our own button doesn't drop the bar first.
      setTimeout(() => {
        const active = document.activeElement;
        if (!isWritableField(active)) {
          fieldRef.current = null;
          setVisible(false);
        } else {
          fieldRef.current = active;
        }
      }, 150);
    };
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, [enabled]);

  const insert = useCallback((ch) => () => {
    if (fieldRef.current) insertCharAtCaret(fieldRef.current, ch);
  }, []);

  if (!enabled || !visible) return null;

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 bottom-2 sm:bottom-3 z-[80] flex flex-wrap gap-1 px-2 sm:px-3 py-1.5 sm:py-2 bg-white border border-stone-300 rounded-2xl sm:rounded-full shadow-lg max-w-[calc(100vw-1rem)] justify-center"
      data-testid="spanish-chars-global"
      role="toolbar"
      aria-label="Spanische Sonderzeichen"
    >
      {CHARS.map((ch) => (
        <button
          key={ch}
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={insert(ch)}
          className="min-w-[36px] sm:min-w-[34px] h-9 sm:h-8 px-2 rounded-lg sm:rounded-full bg-stone-50 border border-stone-200 text-[#292524] text-base sm:text-sm font-semibold leading-none hover:bg-[#AB1C24] hover:text-white hover:border-[#AB1C24] transition-colors touch-manipulation flex items-center justify-center"
          data-testid={`spanish-char-${ch}`}
          aria-label={`Zeichen einfügen: ${ch}`}
        >
          {ch}
        </button>
      ))}
    </div>
  );
}
